import React, { useEffect, useState } from "react";
import {
  FaUserCircle,
  FaStar,
  FaTrophy,
} from "react-icons/fa";
import { BiSolidBadgeCheck } from "react-icons/bi";

const LinearProgress = ({ profileProgress }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let current = 0;
    const target = Math.max(0, Math.min(100, Number(profileProgress) || 0));

    const step = () => {
      current += 1;
      if (current <= target) {
        setProgress(current);
        requestAnimationFrame(step);
      }
    };

    requestAnimationFrame(step);

    return () => setProgress(target);
  }, [profileProgress]);

  const isComplete = progress === 100;

  const styles = `
    @keyframes slideIn {
      from { 
        opacity: 0;
        transform: translateY(-4px);
      }
      to { 
        opacity: 1;
        transform: translateY(0);
      }
    }

    @keyframes shimmer {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }

    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.6; }
    }

    @keyframes checkScale {
      0% { transform: scale(0) rotate(-45deg); }
      50% { transform: scale(1.2) rotate(5deg); }
      100% { transform: scale(1) rotate(0deg); }
    }

    @keyframes sparkle {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.3); opacity: 0.8; }
    }

    .progress-wrapper {
      width: 100%;
      max-width: 320px;
      margin-bottom: 15px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Inter', sans-serif;
      animation: slideIn 0.4s ease-out;
    }

    .progress-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    .progress-label {
      display: flex;
      align-items: center;
      gap: 2px;
    }

    .progress-icon {
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      color: ${isComplete ? "#286a94" : "#286a94"};
      ${isComplete ? "animation: checkScale 0.6s ease-out;" : ""}
    }

    .user-icon {
      font-size: 15px;
      ${!isComplete ? "opacity: 0.8;" : "opacity: 0;"}
      transition: opacity 0.3s ease;
    }

    .check-icon {
      font-size: 20px;
      position: absolute;
      ${
        isComplete
          ? "opacity: 1; animation: checkScale 0.6s ease-out;"
          : "opacity: 0;"
      }
      transition: opacity 0.3s ease;
    }

    .progress-text {
      font-size: 14px;
      font-weight: 600;
      color: ${isComplete ? "#286a94" : "#374151"};
      letter-spacing: -0.02em;
      transition: color 0.4s ease;
    }

    .progress-badge {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 11px;
      font-weight: 600;
      padding: 4px 10px;
      border-radius: 12px;
      transition: all 0.2s ease;
      ${
        isComplete
          ? "background: linear-gradient(135deg, #286a94 0%, #1a4d6d 100%); color: white; box-shadow: 0 2px 8px rgba(40, 106, 148, 0.3);"
          : "background: #f3f4f6; color: #6b7280;"
      }
    }

    .badge-icon {
      font-size: 13px;
      transition: color 0.4s ease;
      ${isComplete ? "animation: sparkle 0.5s ease-in-out infinite;" : ""}
    }

    .progress-track {
      width: 100%;
      height: 8px;
      background: #f3f4f6;
      border-radius: 12px;
      overflow: hidden;
      position: relative;
      box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
    }

    .progress-bar {
      height: 100%;
      width: ${progress}%;
      background: ${
        isComplete
          ? "linear-gradient(90deg, #286a94 0%, #1a4d6d 100%)"
          : "linear-gradient(90deg, #286a94 0%, #00a3c8 100%)"
      };
      border-radius: 12px;
      transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
      box-shadow: ${
        progress > 0
          ? isComplete
            ? "0 0 12px rgba(40, 106, 148, 0.5), 0 2px 4px rgba(40, 106, 148, 0.3)"
            : "0 0 12px rgba(40, 106, 148, 0.4), 0 2px 4px rgba(40, 106, 148, 0.2)"
          : "none"
      };
    }

    .progress-bar::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 50%;
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
      border-radius: 12px 12px 0 0;
    }

    .progress-bar::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
      animation: shimmer 0.5s infinite;
      ${progress >= 100 ? "display: none;" : ""}
    }

    .progress-dots {
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      gap: 3px;
      ${progress < 10 ? "display: none;" : ""}
    }

    .dot {
      width: 2px;
      height: 2px;
      background: rgba(255, 255, 255, 0.6);
      border-radius: 50%;
      animation: pulse 1.5s ease-in-out infinite;
    }

    .dot:nth-child(2) { animation-delay: 0.2s; }
    .dot:nth-child(3) { animation-delay: 0.4s; }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="progress-wrapper">
        <div className="progress-header">
          <div className="progress-label">
            <div className="progress-icon" style={{ position: "relative" }}>
              <FaUserCircle className="user-icon" />
              <BiSolidBadgeCheck className="check-icon" />
            </div>
            <span className="progress-text">Profile Progress</span>
          </div>
          <span className="progress-badge">
            {isComplete ? (
              <>
                <FaTrophy className="badge-icon" />
                <span>Complete</span>
              </>
            ) : (
              <>
                <FaStar
                  className="badge-icon"
                  style={{ opacity: 0.6, color: "gold" }}
                />
                <span>{progress}%</span>
              </>
            )}
          </span>
        </div>

        <div className="progress-track">
          <div className="progress-bar">
            <div className="progress-dots">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default LinearProgress;
